import { useEffect, useState } from 'react';
import {
  IQuoteEditorFormData,
  IQuoteItemDetailsForQuoteManagement,
  IQuotePricingConfig,
  IRequoteParentData,
  ILastQuote
} from 'types/quoteEditor.types';
import { QuoteEditorService } from 'services/quote-editor.service';
import QuoteEditorForm from '../QuoteEditorForm/QuoteEditorForm';
import { BordOneToneIcon } from 'bord-design-system';
import DeviceCard from './DeviceCard/DeviceCard';
import DeviceDescription from './DeviceDescription/DeviceDescription';
import LastQuotes from '../LastQuotes/LastQuotes';
import './RequestedDevice.scss';

interface IRequestedDevice {
  item: IQuoteItemDetailsForQuoteManagement;
  formData: IQuoteEditorFormData;
  countryName: string;
  pricingConfig: IQuotePricingConfig;
  isRequote?: boolean;
  isFormDisabled?: boolean;
  parentData?: IRequoteParentData;
  onFormChange: (data: IQuoteEditorFormData) => void;
}

/**
 * Panel que muestra la herramienta solicitada por el cliente y, si no es genérica,
 * el formulario de cotización directamente. Renderiza cantidad, categoría y pills
 * del producto como grid de especificaciones. Al final muestra las últimas cotizaciones
 * históricas del producto en el país de la cotización.
 */
const RequestedDevice = ({
  item,
  formData,
  countryName,
  pricingConfig,
  isRequote,
  isFormDisabled,
  parentData,
  onFormChange
}: IRequestedDevice) => {
  const { isGenericProduct, quantity, categoryId, productId } = item;

  const [lastQuotes, setLastQuotes] = useState<ILastQuote[]>([]);
  const [isLoadingLastQuotes, setIsLoadingLastQuotes] = useState(false);

  const goToCatalogProductDetails = () => {
    if (!categoryId || !productId) return;
    window.open(`/catalog/create-product/${categoryId}?productId=${productId}`, '_blank');
  };

  /** Carga el historial de precios del SKU en el país de la cotización cuando cambia el producto. */
  useEffect(() => {
    const sku = item.uid;
    const countryCode = pricingConfig.countryCode;
    if (!sku || !countryCode) {
      setLastQuotes([]);
      return;
    }
    setIsLoadingLastQuotes(true);
    QuoteEditorService.getLastQuotesByProduct(sku, countryCode)
      .then(data => setLastQuotes(data ?? []))
      .catch(() => setLastQuotes([]))
      .finally(() => setIsLoadingLastQuotes(false));
  }, [item.uid, pricingConfig.countryCode]);

  return (
    <div className="requestedDevice">
      <div className="requestHeader">
        <BordOneToneIcon variant="info" standardSize={18} stroke="#6F737D" />
        <span className="requestTitle">HERRAMIENTA SOLICITADA POR EL CLIENTE</span>
      </div>
      <DeviceCard item={item} onEditClick={productId ? goToCatalogProductDetails : undefined} />
      <DeviceDescription item={item} />
      {!isGenericProduct && (
        <QuoteEditorForm
          formData={formData}
          requestedUnits={quantity}
          pricingConfig={pricingConfig}
          categoryName={item.categoryName}
          isRequote={isRequote}
          isFormDisabled={isFormDisabled}
          parentData={parentData}
          onChange={onFormChange}
        />
      )}
      <LastQuotes lastQuotes={lastQuotes} country={countryName} isLoading={isLoadingLastQuotes} />
    </div>
  );
};

export default RequestedDevice;
